import {Component, DestroyRef, inject, OnInit, SecurityContext} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DatePipe} from '@angular/common';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {environment} from '../../../../environments/environment';
import {NamedField} from '../../../core/models/categories/named-field.model';

export interface AgentEmailPreview {
  activity_id: number; from: string; to: string[]; cc: string[]; bcc: string[];
  subject: string; html: string; warnings: string[]; errors: string[];
  can_confirm: boolean; review_token: string | null; save_without_automation: boolean; routing: string;
  review_context?: {
    logists: {id: number; name: string; email: string}[];
    quotation_manager: {id: number; name: string; email: string};
    client: {id: number; name: string}; request: {id: number; code: string};
    client_price: {id: number; price: string | number | null; total_price: string | number | null; currency: string | null};
  };
  price_approval_required?: boolean;
  can_approve_price?: boolean;
  price_approval_token?: string | null;
  price_approval?: {approved: boolean; approved_by: string | null; approved_at: string | null};
  price_approval_summary?: {price: string | number | null; currency: string | null} | null;
  price_approval_requirements?: {id: number; source: string; source_id: number; name: string}[];
}

@Component({
  selector: 'app-agent-email-preview-dialog',
  imports: [MatDialogModule, MatButtonModule, DatePipe],
  template: `
    <h2 mat-dialog-title>{{ isClientPrice ? 'Review client prices email' : 'Review agent quotation email' }}</h2>
    <mat-dialog-content>
      @if (approvalOnly) {
        <p>Review and personally approve this saved price. This does not change the Activity status or send an email.</p>
      } @else { <p>Confirm and save changes the Activity status. Personal price approval is a separate action. No email is sent on DEV.</p> }
      @if (busy) { <p role="status">Loading email preview…</p> }
      @if (error) { <p role="alert" class="error">{{ error }}</p><button type="button" mat-button (click)="load()" [disabled]="busy || approving">Retry preview</button> }
      @if (preview; as p) {
        @if (isClientPrice && p.review_context; as context) {
          <section class="review-details" aria-label="Client price review details">
            <dl class="review-context">
              <dt>Request logist(s)</dt><dd>
                @for (logist of context.logists; track logist.id) {
                  <span class="person">{{ logist.name }} @if (logist.email) { <span class="contact">· {{ logist.email }}</span> }</span>
                } @empty { <span>No active logist assigned</span> }
              </dd>
              <dt>Quotation manager</dt><dd>{{ context.quotation_manager.name }} @if (context.quotation_manager.email) { <span class="contact">· {{ context.quotation_manager.email }}</span> }</dd>
              <dt>Client</dt><dd><a [href]="'/dashboard/form/companies/' + context.client.id" target="_blank" rel="noopener noreferrer">{{ context.client.name }}</a></dd>
              <dt>Request</dt><dd><a [href]="'/dashboard/form/requests/' + context.request.id" target="_blank" rel="noopener noreferrer">{{ context.request.code }}</a></dd>
              <dt>Client price</dt><dd><a [href]="'/dashboard/form/client_prices/' + context.client_price.id" target="_blank" rel="noopener noreferrer">Open client price</a>
                <span class="person">Price: {{ context.client_price.price ?? '—' }} {{ context.client_price.currency }}</span>
                <span class="person">Total price: {{ context.client_price.total_price ?? '—' }} {{ context.client_price.currency }}</span>
                @if (p.price_approval?.approved) { <span class="person" role="status">Personally approved by Kristine — {{ p.price_approval?.approved_at | date:'medium' }}</span> }
              </dd>
              @if (p.price_approval_requirements?.length) {
                <dt>Special requirements</dt><dd><ul class="requirements">
                  @for (requirement of p.price_approval_requirements; track $index) {
                    <li><strong>{{ requirement.source }}</strong>: {{ requirement.name }}</li>
                  }
                </ul></dd>
              }
            </dl>
          </section>
        }
        @if (p.price_approval_required) {
          <section class="approval" role="alert" aria-labelledby="price-approval-title">
            <h3 id="price-approval-title">{{ p.price_approval?.approved ? 'Price approved' : 'Price approval required' }}</h3>
          </section>
        }
        <dl><dt>From</dt><dd>{{ p.from }}</dd><dt>To</dt><dd>{{ p.to.join(', ') }}</dd>
          <dt>CC</dt><dd>{{ p.cc.join(', ') || '—' }}</dd><dt>BCC</dt><dd>{{ p.bcc.join(', ') || '—' }}</dd>
          <dt>Subject</dt><dd>{{ p.subject }}</dd></dl>
        @if (!isClientPrice) {
          @for (warning of p.warnings; track $index) { <p class="warning">{{ warning }}</p> }
        }
        @for (message of p.errors; track $index) { <p class="error" role="alert">{{ message }}</p> }
        <iframe title="Email body preview" sandbox="" referrerpolicy="no-referrer" [srcdoc]="safeHtml"></iframe>
        @if (!p.save_without_automation) { <p>Confirming saves Ready to do. The automatic sender may process this email afterward.</p> }
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button type="button" mat-stroked-button class="btn" [disabled]="approving" (click)="dialog.close()">{{ approvalOnly ? 'Close' : 'Back to edit' }}</button>
      @if (isClientPrice && preview?.price_approval_required && !preview?.price_approval?.approved && preview?.can_approve_price && preview?.price_approval_token) {
        <button type="button" mat-raised-button color="primary" class="btn" (click)="approvePrice()" [disabled]="busy || approving || confirmed || !!error">{{ approving ? 'Approving…' : 'Approve' }}</button>
      }
      @if (!approvalOnly) {
      <button type="button" mat-raised-button color="primary" class="btn" [disabled]="busy || approving || !!error || !preview?.can_confirm || !preview?.review_token || confirmed || (preview?.price_approval_required && !preview?.price_approval?.approved)" (click)="confirm()">
        {{ preview?.save_without_automation ? 'Confirm and save (no email on DEV)' : 'Confirm and save' }}
      </button>
      }
    </mat-dialog-actions>
  `,
  styles: `
    mat-dialog-actions { gap:10px; }
    mat-dialog-actions .btn { border-radius:4px; height:30px; box-shadow:unset; }
    :host { display:block; } dl { display:grid; grid-template-columns:70px minmax(0,1fr); gap:8px 12px; }
    dt { font-weight:600; } dd { margin:0; overflow-wrap:anywhere; white-space:pre-wrap; }
    iframe { width:100%; height:420px; border:1px solid #d9e5e4; background:white; }
    .warning { padding:10px; background:#fff8de; border-radius:5px; } .error { color:#a40000; }
    .approval { padding:16px; margin-bottom:16px; border:2px solid #b43f00; border-radius:6px; background:#fff0df; }
    .review-details { padding:12px 16px; margin-bottom:16px; border:1px solid #d9e5e4; border-radius:6px; background:#f6faf9; }
    .review-context { grid-template-columns:135px minmax(0,1fr); margin:0; } .person { display:block; } .contact { color:#52615f; }
    .review-details a { color:#007e78; text-decoration:underline; }
    .requirements { margin:0; padding-left:18px; list-style:disc; } .requirements li + li { margin-top:4px; }
    .approval h3 { margin:0; color:#8d2800; } .approval-check { display:flex; gap:10px; align-items:flex-start; font-weight:600; }
    @media(max-width:600px) { iframe { height:320px; } }
  `,
})
export class AgentEmailPreviewDialogComponent implements OnInit {
  readonly dialog = inject(MatDialogRef<AgentEmailPreviewDialogComponent>);
  private readonly data = inject<{activityId: number | null; draft: NamedField; kind?: 'agent' | 'client'; approvalOnly?: boolean}>(MAT_DIALOG_DATA);
  readonly isClientPrice = this.data.kind === 'client';
  readonly approvalOnly = this.isClientPrice && (environment.production || this.data.approvalOnly === true);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  preview: AgentEmailPreview | null = null;
  safeHtml: SafeHtml | null = null;
  busy = false;
  confirmed = false;
  approving = false;
  error = '';

  ngOnInit(): void { this.load(); }

  load(): void {
    if (this.busy || this.approving) return;
    this.preview = null; this.safeHtml = null; this.error = ''; this.approving = false;
    if (!this.data.activityId) { this.error = 'Save this Activity as Upcoming first, then preview it before selecting Ready to do.'; return; }
    this.busy = true;
    this.http.post<{data: AgentEmailPreview}>(environment.baseApi + 'entity/search', {
      entity: 'activities', related_entity: 'activities', data: {field: 'template_content', page: 1,
        record_id: this.data.activityId, [this.isClientPrice ? 'client_price_email_preview' : 'agent_quotation_email_preview']: true, email_draft: this.data.draft},
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({data}) => {
        this.preview = data;
        // Sanitize first, then isolate mail content: no scripts, forms, links or remote tracking images.
        const clean = this.sanitizer.sanitize(SecurityContext.HTML, data.html) ?? '';
        const document = new DOMParser().parseFromString(clean, 'text/html');
        document.querySelectorAll('a').forEach(link => { link.removeAttribute('href'); link.removeAttribute('target'); });
        const policy = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; form-action 'none'; base-uri 'none'";
        this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(`<html><head><meta http-equiv="Content-Security-Policy" content="${policy}"><meta name="referrer" content="no-referrer"></head><body>${document.body.innerHTML}</body></html>`);
        this.busy = false;
      },
      error: err => {
        this.busy = false;
        const errors = err?.error?.errors ?? err?.error?.data?.errors;
        const message = errors && Object.values(errors).flat().find(value => typeof value === 'string');
        this.error = typeof message === 'string' ? message : 'Could not load the email preview. Check your access and try again. Nothing was saved or sent.';
      },
    });
  }

  approvePrice(): void {
    if (!this.isClientPrice || this.busy || this.approving || this.error || this.confirmed || !this.preview?.can_approve_price || !this.preview.price_approval_token) return;
    this.approving = true; this.error = '';
    this.http.post<{data: AgentEmailPreview}>(environment.baseApi + 'entity/search', {
      entity: 'activities', related_entity: 'activities', data: {field: 'template_content', page: 1,
        record_id: this.data.activityId, client_price_email_preview: true, personal_price_approval: true,
        price_approval_token: this.preview.price_approval_token, email_draft: this.data.draft},
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.approving = false; this.load(); },
      error: err => { this.approving = false; this.error = err?.status === 403
        ? 'Only Kristine can personally approve this price.' : 'Could not approve this price. Reload the preview; the price or requirements may have changed.'; },
    });
  }

  confirm(): void {
    if (this.approvalOnly || this.busy || this.approving || this.error || !this.preview?.can_confirm || !this.preview.review_token || this.confirmed || (this.preview.price_approval_required && !this.preview.price_approval?.approved)) return;
    this.confirmed = true;
    this.dialog.close(this.preview.review_token);
  }
}
